export class TimestampParseError extends Error {
    input;
    constructor(input, message) {
        super(message);
        this.input = input;
        this.name = "TimestampParseError";
    }
}
const MINUTE_SECOND_RE = /^(\d+):(\d{2})(?:\.(\d{1,3}))?$/;
const HOUR_MINUTE_SECOND_RE = /^(\d+):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/;
function fractionMilliseconds(raw) {
    return raw ? Number(raw.padEnd(3, "0")) : 0;
}
function requireSafeMilliseconds(raw, milliseconds) {
    if (!Number.isSafeInteger(milliseconds)) {
        throw new TimestampParseError(raw, "Timestamp exceeds the supported numeric range.");
    }
    return milliseconds;
}
function canonicalTimestamp(milliseconds, fraction) {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const fractionPart = fraction ? `.${fraction}` : "";
    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}${fractionPart}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}${fractionPart}`;
}
export function parseTimestamp(raw) {
    const input = raw.trim();
    if (!input) {
        throw new TimestampParseError(raw, "Timestamp must not be empty.");
    }
    const hourMatch = input.match(HOUR_MINUTE_SECOND_RE);
    if (hourMatch) {
        const hours = Number(hourMatch[1]);
        const minutes = Number(hourMatch[2]);
        const seconds = Number(hourMatch[3]);
        const fraction = hourMatch[4];
        if (minutes > 59) {
            throw new TimestampParseError(raw, "Hour-form timestamps require minutes between 00 and 59.");
        }
        if (seconds > 59) {
            throw new TimestampParseError(raw, "Timestamp seconds must be between 00 and 59.");
        }
        const milliseconds = requireSafeMilliseconds(raw, ((hours * 60 + minutes) * 60 + seconds) * 1000 + fractionMilliseconds(fraction));
        return {
            canonical: canonicalTimestamp(milliseconds, fraction),
            milliseconds,
            fractionDigits: fraction?.length ?? 0,
        };
    }
    const minuteMatch = input.match(MINUTE_SECOND_RE);
    if (!minuteMatch) {
        throw new TimestampParseError(raw, "Use M:SS, MM:SS, or H:MM:SS with an optional 1-3 digit fraction.");
    }
    const minutes = Number(minuteMatch[1]);
    const seconds = Number(minuteMatch[2]);
    const fraction = minuteMatch[3];
    if (seconds > 59) {
        throw new TimestampParseError(raw, "Timestamp seconds must be between 00 and 59.");
    }
    const milliseconds = requireSafeMilliseconds(raw, (minutes * 60 + seconds) * 1000 + fractionMilliseconds(fraction));
    return {
        canonical: canonicalTimestamp(milliseconds, fraction),
        milliseconds,
        fractionDigits: fraction?.length ?? 0,
    };
}
export function parseTimestampRange(raw) {
    const input = raw.trim();
    const parts = input.split("..");
    if (parts.length > 2 || parts.some((part) => part.trim() === "")) {
        throw new TimestampParseError(raw, "Use one timestamp or a closed range such as 00:10..00:20.");
    }
    const first = parseTimestamp(parts[0]);
    const second = parts.length === 2 ? parseTimestamp(parts[1]) : first;
    return first.milliseconds <= second.milliseconds
        ? { start: first, end: second }
        : { start: second, end: first };
}
export function timestampInRange(timestamp, range) {
    return timestamp.milliseconds >= range.start.milliseconds
        && timestamp.milliseconds <= range.end.milliseconds;
}
function timestampOfAction(action) {
    if (typeof action !== "object" || action === null || Array.isArray(action))
        return undefined;
    const extend = action.extend;
    if (typeof extend !== "object" || extend === null || Array.isArray(extend))
        return undefined;
    return extend.timestamp;
}
/**
 * Action timestamps mark simultaneous beats as well as sequential ones, so
 * equal timestamps are valid. Only a later Action moving backwards is invalid.
 */
export function validateActionTimestampOrder(actions, actionsPath, issue) {
    let previous = null;
    actions.forEach((action, actionIndex) => {
        const rawTimestamp = timestampOfAction(action);
        if (rawTimestamp === undefined)
            return;
        const timestampPath = `${actionsPath}[${actionIndex}].extend.timestamp`;
        if (typeof rawTimestamp !== "string") {
            issue("timestamp_invalid", timestampPath, "timestamp must be a string in M:SS, MM:SS, or H:MM:SS form");
            return;
        }
        let timestamp;
        try {
            timestamp = parseTimestamp(rawTimestamp);
        }
        catch (error) {
            if (!(error instanceof TimestampParseError))
                throw error;
            issue("timestamp_invalid", timestampPath, error.message);
            return;
        }
        if (previous !== null && timestamp.milliseconds < previous.timestamp.milliseconds) {
            issue("timestamp_out_of_order", timestampPath, `timestamp ${timestamp.canonical} is earlier than Action ${previous.actionIndex + 1} timestamp ${previous.timestamp.canonical}; equal timestamps are allowed`);
        }
        previous = { actionIndex, timestamp };
    });
}
